export interface IDescriptionTile {
  id: string;
  title: string | null;
  body: string;
  /** Set where the tile is a list, so it renders as one rather than as a run of lines. */
  items?: string[];
}

export interface IParsedDescription {
  intro: string;
  tiles: IDescriptionTile[];
}

const BLOCK_SEPARATOR = /\n\s*\n/;
const HEADER_LINE = /^(.{1,60}?):[ \t]*\r?\n([\s\S]*)$/;
const INLINE_LABEL = /^([A-Z][A-Z0-9 &'’\-/()]{1,50}?):[ \t]*([\s\S]*)$/;

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  apos: "'",
  gt: '>',
  lt: '<',
  nbsp: ' ',
  quot: '"',
};

/**
 * Supplier content reaches us HTML-escaped ("Mickey&#39;s"), and it is rendered as
 * text rather than markup, so the escaping only shows up as literal noise. Ampersand
 * last, so a double-escaped "&amp;#39;" resolves to "&#39;" and not to an apostrophe.
 */
export const decodeHtmlEntities = (text: string): string =>
  text
    .replace(/&#x([0-9a-f]+);/gi, (_, hex) => String.fromCodePoint(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_, dec) => String.fromCodePoint(Number(dec)))
    .replace(/&(apos|quot|nbsp|lt|gt);/gi, (match, name) => NAMED_ENTITIES[name.toLowerCase()] ?? match)
    .replace(/&amp;/gi, NAMED_ENTITIES.amp);

/**
 * Folds the hard wrapping some suppliers apply at around 70 columns. Those newlines
 * are typographic rather than structural, and reading them as line breaks makes a
 * paragraph look like dozens of one-line blocks. Blank lines are left alone, since
 * those do separate blocks.
 */
export const unwrapHardBreaks = (text: string): string =>
  text
    .replace(/\r\n/g, '\n')
    .split(BLOCK_SEPARATOR)
    .map((block) => block.replace(/\s*\n\s*/g, ' ').replace(/[ \t]{2,}/g, ' ').trim())
    .filter((block) => !!block)
    .join('\n\n');

/**
 * A label opening a section mid-paragraph: "... dining room. Game Loft: Head upstairs".
 * Anchored to the end of the previous sentence so the colons inside a run-on list
 * ("Biscayne Bay, including: ...") are not mistaken for one.
 */
const INLINE_SECTION = /(^|[.!?)”"’']\s+)([A-Z][A-Za-z0-9&'’\-/(). ,]{0,45}?):[ \t]+(?=[A-Z0-9“"])/g;

// A section title is a name, not a sentence, and what follows it is a paragraph. Both
// bounds are what keep an aside such as "Think: a pool table, dart board." out.
const MAX_TITLE_WORDS = 5;
const MIN_SECTION_BODY = 60;
// Below this, whatever matched is more likely to be prose punctuation than a format.
const MIN_SECTIONS = 3;

/**
 * Reads sections out of a single run of prose. Returns nothing unless the text is
 * laid out this way throughout, so an unrecognised format falls through to being
 * shown as prose rather than being carved into a grid of fragments.
 */
const readInlineSections = (text: string): { sections: IDescriptionTile[]; leadEnd: number } => {
  const marks: { title: string; start: number; bodyStart: number }[] = [];

  Array.from(text.matchAll(INLINE_SECTION)).forEach((match) => {
    const title = match[2].trim();
    const index = match.index ?? 0;

    // Bulleted lines ("• SeaWorld: ~26 minute drive") are content, not headings.
    if (title.split(/\s+/).length > MAX_TITLE_WORDS || /[•·]/.test(match[0])) {
      return;
    }

    marks.push({
      title,
      start: index + match[1].length,
      bodyStart: index + match[0].length,
    });
  });

  const sections: IDescriptionTile[] = [];
  let leadEnd = text.length;

  marks.forEach((mark, index) => {
    const end = index + 1 < marks.length ? marks[index + 1].start : text.length;
    const body = text.slice(mark.bodyStart, end).trim();

    if (body.length < MIN_SECTION_BODY) {
      return;
    }

    if (!sections.length) {
      leadEnd = mark.start;
    }

    sections.push({
      id: `description-section-${sections.length}`,
      title: formatTileTitle(mark.title),
      body,
    });
  });

  return sections.length >= MIN_SECTIONS ? { sections, leadEnd } : { sections: [], leadEnd: 0 };
};

const SPLIT_MARK = '\u0000';

/**
 * Where a supplier's HTML has had its tags stripped, the delimiters went with them and
 * the text runs together: "up to 14 people.Home Features-7 Bedrooms5.5 Baths". Each of
 * those joins is still visible, because prose always puts a space before a capital, a
 * digit or a new sentence, and this text does not.
 */
const markLostDelimiters = (text: string): string =>
  text
    .replace(/([a-z)"'’])([A-Z0-9])/g, `$1${SPLIT_MARK}$2`)
    // A sentence closing straight onto the next one. Capitals only, so "5.5" survives.
    .replace(/\.([A-Z])/g, `.${SPLIT_MARK}$1`)
    // All that is left of a list marker: "Home Features-7 Bedrooms".
    .replace(/([a-z])-(?=\d)/g, `$1${SPLIT_MARK}`);

// "SeaWorld" and "CocoWalk" read as joins too, so the rule is only trusted where the
// text is riddled with them. Well-formed descriptions carry one or two in several
// thousand characters; a stripped one carries dozens.
const MIN_LOST_DELIMITERS = 8;
const MAX_CHARS_PER_DELIMITER = 400;

export const looksDelimiterStripped = (text: string): boolean => {
  const count = markLostDelimiters(text).split(SPLIT_MARK).length - 1;

  return count >= MIN_LOST_DELIMITERS && text.length / count <= MAX_CHARS_PER_DELIMITER;
};

// A list item is a name or a phrase; a paragraph is sentences. The cap only guards
// against a paragraph whose full stop the supplier also dropped.
const MAX_ITEM_WORDS = 30;
const MAX_HEADING_WORDS = 4;
// A heading plus enough beneath it to be a list rather than a coincidence.
const MIN_LIST_ITEMS = 3;

const ENDS_SENTENCE = /[.!?]["'’)]?$/;

const countWords = (text: string): number => text.split(/\s+/).length;

const isListItem = (text: string): boolean =>
  !ENDS_SENTENCE.test(text) && countWords(text) <= MAX_ITEM_WORDS;

const isHeading = (text: string): boolean => countWords(text) <= MAX_HEADING_WORDS;

// "1-King (Downstairs, Owner's Ensuite)". A quantity and a dash is how a configuration
// is written and not how an amenity is, which is what makes a run of them recognisable.
const QUANTIFIED_ITEM = /^\d+\s*[-–]\s*\S/;

/**
 * Finds where a second list starts inside one run, which happens when the heading
 * between them lost its markup along with everything else and now reads as an item.
 * Only a run of quantified items counts, since anything looser would take the first
 * item of an ordinary list for a heading.
 */
const findNestedHeading = (run: string[]): number => {
  const start = run.findIndex(
    (item, index) =>
      index > 0 &&
      QUANTIFIED_ITEM.test(item) &&
      !QUANTIFIED_ITEM.test(run[index - 1]) &&
      !/\d/.test(run[index - 1]) &&
      isHeading(run[index - 1]) &&
      run.slice(index, index + MIN_LIST_ITEMS).filter((next) => QUANTIFIED_ITEM.test(next)).length >=
        MIN_LIST_ITEMS,
  );

  return start > 1 ? start - 1 : -1;
};

/**
 * Rebuilds the headings and lists of a description whose markup was stripped. Runs of
 * short unpunctuated fragments are the lists; the fragment above each run is its
 * heading, and everything else is the prose it was written around.
 */
const readStrippedSections = (text: string): IParsedDescription | null => {
  const fragments = markLostDelimiters(text)
    .split(SPLIT_MARK)
    .map((fragment) => fragment.replace(/^[\s\-–—•·]+/, '').trim())
    .filter((fragment) => !!fragment);

  const paragraphs: string[] = [];
  const tiles: IDescriptionTile[] = [];
  let run: string[] = [];

  const addList = (list: string[]): void => {
    if (list.length <= MIN_LIST_ITEMS) {
      // Too short to read as a list, so it stays part of the prose around it.
      paragraphs.push(...list);

      return;
    }

    const [first, ...rest] = list;
    const hasHeading = isHeading(first);
    const items = hasHeading ? rest : list;

    tiles.push({
      id: `description-list-${tiles.length}`,
      title: hasHeading ? formatTileTitle(first) : null,
      body: items.join('\n'),
      items,
    });
  };

  const closeRun = (): void => {
    const nested = findNestedHeading(run);

    if (nested > 0) {
      addList(run.slice(0, nested));
      addList(run.slice(nested));
    } else {
      addList(run);
    }

    run = [];
  };

  fragments.forEach((fragment) => {
    if (isListItem(fragment)) {
      run.push(fragment);

      return;
    }

    closeRun();
    paragraphs.push(fragment);
  });

  closeRun();

  if (!tiles.length) {
    return null;
  }

  return { intro: paragraphs.join('\n\n'), tiles };
};

const TITLE_STOP_WORDS = [
  'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'from', 'in',
  'nor', 'of', 'on', 'or', 'so', 'the', 'to', 'up', 'with', 'yet',
];

export const toDescriptionTitleCase = (raw: string): string =>
  raw
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .map((word, index) =>
      index > 0 && TITLE_STOP_WORDS.includes(word)
        ? word
        : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(' ');

/**
 * Shouted labels ("MASTER BEDROOM") are the reason for the title casing, and one a
 * supplier already cased is left exactly as written: re-casing it only damages the
 * letters this rule cannot see, as in "Entrance/Great Room" or "Bedrooms (King)".
 */
const formatTileTitle = (raw: string): string =>
  /[a-z]/.test(raw) ? raw.trim() : toDescriptionTitleCase(raw);

export interface IPartitionedTiles {
  locationTiles: IDescriptionTile[];
  propertyTiles: IDescriptionTile[];
}

// Compared without punctuation or case so a supplier writing "YOU'RE" with a curly
// apostrophe still matches the same section as one writing it straight.
const normaliseTitle = (title: string): string => title.toLowerCase().replace(/[^a-z0-9]/g, '');

const LOCATION_TILE_TITLES = new Set(
  ['Location', "What to Do While You're Here", 'Getting Around', 'The Neighborhood'].map(
    normaliseTitle,
  ),
);

/**
 * Separates the tiles that describe the surrounding area from those describing the
 * property itself, so the area ones can sit with the map instead of the overview.
 */
export const partitionDescriptionTiles = (tiles: IDescriptionTile[]): IPartitionedTiles => {
  const locationTiles: IDescriptionTile[] = [];
  const propertyTiles: IDescriptionTile[] = [];

  tiles.forEach((tile) => {
    const isLocationTile = !!tile.title && LOCATION_TILE_TITLES.has(normaliseTitle(tile.title));

    (isLocationTile ? locationTiles : propertyTiles).push(tile);
  });

  return { locationTiles, propertyTiles };
};

/**
 * Splits a vacation rental description into a lead paragraph and a set of tiles.
 *
 * Suppliers label blocks two ways: a title on its own line ("Guest Access:\n...")
 * or an upper-case label inline ("KITCHEN: ..."). The inline rule is applied after
 * the header rule so a bare section heading such as "The Space:" gives way to the
 * more specific label that follows it. Blocks matching neither are continuations
 * of the block above and are merged into it rather than left as untitled tiles.
 *
 * Where a supplier sends no blank lines at all, the text is re-read as one run of
 * prose: first as stripped markup, whose lists are rebuilt from the delimiters they
 * lost, and failing that as prose carrying inline section labels. Both passes are
 * deliberately after the block one, since unwrapping the hard breaks they need would
 * destroy the "title on its own line" layout above. Anything no pass recognises is
 * returned whole, for the caller to show as prose.
 */
export const parseVacationRentalDescription = (
  descriptions: string[] | null | undefined,
): IParsedDescription => {
  const text = decodeHtmlEntities(
    (descriptions || [])
      .filter((entry) => typeof entry === 'string' && !!entry.trim())
      .join('\n\n'),
  );

  const blocks = text
    .split(BLOCK_SEPARATOR)
    .map((block) => block.trim())
    .filter((block) => !!block);

  if (!blocks.length) {
    return { intro: '', tiles: [] };
  }

  const tiles: IDescriptionTile[] = [];

  blocks.slice(1).forEach((block, index) => {
    let title: string | null = null;
    let body = block;

    const headerMatch = block.match(HEADER_LINE);
    if (headerMatch) {
      title = headerMatch[1].trim();
      body = headerMatch[2].trim();
    }

    const inlineMatch = body.match(INLINE_LABEL);
    if (inlineMatch) {
      title = inlineMatch[1].trim();
      body = inlineMatch[2].trim();
    }

    const previousTile = tiles[tiles.length - 1];
    if (!title && previousTile) {
      previousTile.body = `${previousTile.body}\n\n${body}`;
      return;
    }

    tiles.push({
      id: `description-tile-${index}`,
      title: title ? formatTileTitle(title) : null,
      body,
    });
  });

  if (tiles.length) {
    return { intro: blocks[0], tiles };
  }

  const prose = unwrapHardBreaks(text);

  if (looksDelimiterStripped(prose)) {
    const recovered = readStrippedSections(prose);

    if (recovered) {
      return recovered;
    }
  }

  const { sections, leadEnd } = readInlineSections(prose);

  if (!sections.length) {
    return { intro: prose, tiles: [] };
  }

  // Whatever precedes the first label, which is nothing when the text opens on one.
  return { intro: prose.slice(0, leadEnd).trim(), tiles: sections };
};
